import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Row} from "reactstrap";
import {useTranslation} from "react-i18next";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {DataGridColumnAlignments, DataGridColumnTypes, DataGridExoticColumnFields, useDataGridSavedState} from "../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../containers/data-grid";
import {AccountReceivableTransactionsDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import IconButton from "../../../../../components/base/button/icon_button";
import {ReceiptLongRounded} from "@mui/icons-material";
import TransactionReceiptDialog from "../../../../../components/dialogs/transaction-receipt-dialog";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {useAppDispatch, useAppSelector} from "../../../../../redux";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import useSearch from "../../../../../hooks/use-search";
import {formatMoney} from "../../../../../../core/services/utils/utils";
import DateUtils from "../../../../../../core/services/date-utils";
import {Alert, AlertTitle, Box, Card, Divider, Skeleton, SvgIcon, Typography} from "@mui/material";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useQuery from "../../../../../hooks/use-query";
import Button from "../../../../../components/base/button/button";
import {ReactComponent as Printer} from "../../../../../../assets/images/printer.svg";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import ToastService from "../../../../../../core/services/toast";
import PrintCustomerOutstandingStatementDialog from "../../../../../components/dialogs/print-customer-outstanding-statement";
import Grid2 from "@mui/material/Unstable_Grid2";
import Api from "../../../../../../core/services/api/api";
import Form, {MuiRFF, Yup} from "../../../../../components/form";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import useInfiniteScrollSearchUtils from "../../../../../hooks/use-infinite-scroll-search-utils";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import {usePartnerInformation} from "../../../../../hooks/use-partner-information";
import {useOperatorInformation} from "../../../../../hooks/use-operator-information";

const dataGridSaveConfig = {
    name: 'AccountReceivableTransactionsDataGrid',
    version: 2,
}


const dataGridActions = {
    connect: "connect",
    requestDisconnection: "requestDisconnection",
}

const tabs = {
    outstandingBalance: "remainingBalance",
    all: "all",
};

const defaultTab = tabs.outstandingBalance;

const initReceiptDialogState = {title: '', open: false, customerId: undefined, receiptId: undefined, close: () => void 0};
const initialStatementPrintingDialogState = {open: false};

/**
 * Dedupes AR transaction items by recordId (first occurrence wins).
 * Items without a recordId are kept in order without collapsing together.
 * @param {Array} items
 * @returns {Array}
 */
const uniqueByRecordId = (items = []) => {
    const seen = new Set();
    const result = [];
    for (const item of items) {
        const id = item?.recordId;
        if (id == null) {
            result.push(item);
            continue;
        }
        if (seen.has(id)) {
            continue;
        }
        seen.add(id);
        result.push(item);
    }
    return result;
};

const CustomerAccountReceivableTransactionsView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.customerAccountReceivableTransactions'});
    const [data, setData] = useState([]);
    const [receiptDialogState, setReceiptDialogState] = useState(initReceiptDialogState);
    const [statementPrintingDialogState, setStatementPrintingDialogState] = useState(initialStatementPrintingDialogState);
    const [selectedTransactions, setSelectedTransactions] = useState([]);
    const operatorAccessLevels = useAccessLevels();
    const [accountReceivableInformation, setAccountReceivableInformation] = useState({});
    const [loadingAccountReceivableInformation, setLoadingAccountReceivableInformation] = useState(true);
    const [arInfoError, setArInfoError] = useState(null);
    const disabledRef = useRef(false);
    const [query] = useQuery()
    const [updatedTab, setUpdatedTab] = useState(query.tab ?? defaultTab);
    const isMounted = useIsMounted();
    const dispatch = useAppDispatch();
    /**@type {React.MutableRefObject<DataGridController>}*/
    const dataGridApi = useRef();
    const partnerInformation = usePartnerInformation();
    const operatorInformation = useOperatorInformation();

    const toCents = (value) => Math.round((Number(value) || 0) * 100);

    const totalAmount = useMemo(() => {
        return selectedTransactions.reduce((acc, item) => {
            return acc + toCents(item.debit) - toCents(item.credit);
        }, 0) / 100;
    }, [selectedTransactions]);

    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const {configureNotebookTags} = useConfigureNotebook('customer-account-receivable-transactions');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool
     */
    useEffect(() => {
        if (defaultCustomer?.code) {
            configureNotebookTags((tags) => tags.customerAccountReceivableTransactions(defaultCustomer?.code));
            configureBehavioralTool((tags) => tags.customerAccountReceivableTransactions(defaultCustomer?.code));
        } else {
            configureNotebookTags((tags) => tags.salesInvoices);
            configureBehavioralTool((tags) => tags.salesInvoices);
        }
    }, [configureNotebookTags, configureBehavioralTool, defaultCustomer]);

    /**
     * Fetches the account receivable information for the selected customer to be used in the view header.
     * On failure, stays on the page and surfaces a retryable error instead of navigating away.
     * @type {(function(): Promise<undefined|*>)|*}
     */
    const fetchAccountReceivableInformation = useCallback(async () => {
        setLoadingAccountReceivableInformation(true);
        const response = await Api.getARAgedList({
            keyword: defaultCustomer?.code,
            pagination: {currentPage: 1, pageSize: 10},
        });
        if (!isMounted())
            return;
        const errorMessage = t('arInfoFetchError', {code: defaultCustomer?.code ?? '--'});
        if (!response?.resultFlag) {
            setArInfoError(errorMessage);
            setLoadingAccountReceivableInformation(false);
            return;
        }
        const foundAr = response?.data?.items?.find(ar => ar.customerId === defaultCustomer?.id);
        if (!foundAr) {
            setArInfoError(errorMessage);
            setLoadingAccountReceivableInformation(false);
            return;
        }
        setArInfoError(null);
        setLoadingAccountReceivableInformation(false);
        setAccountReceivableInformation(foundAr);
    }, [defaultCustomer?.code, defaultCustomer?.id, isMounted, t])

    /**
     * With the change of the selected customer, fetches the account receivable information for the customer to ensure that there is AR information for the customer and to get the necessary information for the header and error handling.
     */
    useEffect(() => {
        if (defaultCustomer?.id) {
            setArInfoError(null);
            void fetchAccountReceivableInformation();
        }
    }, [defaultCustomer?.id, fetchAccountReceivableInformation])


    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: DataGridExoticColumnFields.selection,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                resizable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                reorderable: false,
                sortable: false,
                pinned: true,
            },
            ...(operatorAccessLevels.packages.sales.paymentReceipts ? [
                {
                    name: AccountReceivableTransactionsDataGridColumnNames.receipt,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    pinned: true,
                    pinnedToggleable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    reorderable: false,
                    width: 0,
                },
            ] : []),
            {
                title: "Transaction No",
                name: AccountReceivableTransactionsDataGridColumnNames.transactionNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 120,
                sortable: false,
            },
            {
                title: "Date",
                name: AccountReceivableTransactionsDataGridColumnNames.date,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                sortable: false,
            },
            {
                title: "Debit",
                name: AccountReceivableTransactionsDataGridColumnNames.debit,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Credit",
                name: AccountReceivableTransactionsDataGridColumnNames.credit,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Link Balance",
                name: AccountReceivableTransactionsDataGridColumnNames.linkBalance,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Balance",
                name: AccountReceivableTransactionsDataGridColumnNames.balance,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Terms Code",
                name: AccountReceivableTransactionsDataGridColumnNames.termsCode,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Customer PO",
                name: AccountReceivableTransactionsDataGridColumnNames.customerPO,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 150,
            },
            {
                title: "Reference No",
                name: AccountReceivableTransactionsDataGridColumnNames.referenceNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Type",
                name: AccountReceivableTransactionsDataGridColumnNames.type,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Memo",
                name: AccountReceivableTransactionsDataGridColumnNames.memo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 200,
            },
        ], [operatorAccessLevels.packages.sales.paymentReceipts])

    /**
     * Searches for list of records.
     * Page 1 replaces (unique by recordId); later pages append only new recordIds.
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = {
            pagination: args.paginationInfo,
            sorts: args.orderBy ? [{property: args.orderBy.field, isDesc: args.orderBy.descending}] : null,
            customerId: defaultCustomer?.id,
            showOutstandingOnly: args.tab === tabs.outstandingBalance,
            startDateTime: args.filters?.startDate,
            rndDateTime: args.filters?.endDate,
        }
        const response = await Api.listArTransactions(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            const incoming = response?.data?.items ?? [];
            setData(prevState => {
                const newState = args.paginationInfo.currentPage === 1
                    ? uniqueByRecordId(incoming)
                    : uniqueByRecordId([...prevState, ...incoming]);
                dataRef.current = newState;
                return newState;
            });
            args.setPaginationInfo(prevState => {
                const newState = ({
                    ...prevState,
                    length: response?.data?.totalCount ?? 0
                })
                paginationInfoRef.current = newState;
                return newState;
            })
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [defaultCustomer?.id, isMounted])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        if (disabledRef.current) {
            return;
        }
        await fetchEntries(result, false);
    }, [fetchEntries]);


    const [dataGridColumns, dataGridSortBy, , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => ({...DefaultPaginationInfo, pageSize: 30}), [])

    const initialSearchDataSettings = useMemo(() => ({
        tab: updatedTab,
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {},
    }), [initialPaginationInfo, dataGridSortBy, updatedTab]);

    const searchData = useSearch(search, initialSearchDataSettings, false,)
    const canExportCustomerAccountReceivableTransactions = operatorAccessLevels.packages.sales.exportCustomerAccountReceivableTransactions;
    const paginationInfoRef = useRef(searchData.paginationInfo);
    paginationInfoRef.current = searchData.paginationInfo;
    const {setTab, performSearch, setFilters, setLoading, setPaginationInfo} = searchData;

    const dataRef = useRef(data);
    dataRef.current = data;
    const {hasMoreToLoad, loadMoreData} = useInfiniteScrollSearchUtils({
        dataLength: data.length,
        searchData: searchData,
    });

    /**
     * Handles the change of the current page in the pagination of the data-grid to load more data when the user reaches the end of the table.
     * @type {(function(*, *, *): void)}
     */
    const onCurrentPageChanged = useCallback((_, __, change) => {
        if (searchData.loading || !hasMoreToLoad)
            return;
        loadMoreData(change);
    }, [hasMoreToLoad, loadMoreData, searchData.loading]);

    /**
     * Formats the list of lost search reports into csv format
     * @param list
     * @return {{title: string, body: (*|*[])}}
     */
    const formatExportingData = useCallback((list) => {
        const exportTitle = `${defaultCustomer?.name ?? "Customer"} AR Transactions - ${DateUtils.localNow().format(DateUtils.dateTimeFormat)}`;
        return {
            title: exportTitle,
            body: list?.map((item) => ({
                "Transaction Number": item.transactionNo,
                "Date": item.date ? DateUtils.serverToLocal(item.date).format(DateUtils.dateTimeFormat) : '',
                "Debit": item.debit ? formatMoney(item.debit) : '',
                "Credit": item.credit ? formatMoney(item.credit) : '',
                "Balance": item.balance ? formatMoney(item.balance) : '',
                "Applied": item.linkBalance ? formatMoney(item.linkBalance) : '',
                "Terms Code": item.termsCode ?? '',
                "Customer PO": item.customerPO ?? '',
                "Reference No": item.referenceNo ?? '',
                "Type": item.type ?? '',
                "Memo": item.memo ?? '',
                "Customer Number": item.customer_Number ?? '',
                "Customer Name": item.customer_Name ?? '',
            })) ?? [],
        }
    }, [defaultCustomer?.name])

    /**
     * Loads all the data of the current search filters without pagination to be used in the exporting functionality to export all the data that matches the search filters instead of just the data in the current page.
     */
    const loadAllData = useCallback(async (unique = true) => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: 100,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res.data.items);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        const uniqueValues = Array.from(new Set(entries.map(item => item.recordId))).map(id => entries.find(item => item.recordId === id));
        if (unique) {
            if (uniqueValues.length !== entries.length) {
                // show duplicates
                console.log("duplicate entries", entries.filter((item, index, self) => index !== self.findIndex(i => i.recordId === item.recordId)));
            }
            return uniqueValues;
        }
        return entries;
    }, [searchData, fetchEntries, isMounted]);

    /**
     * Exports the requests using the same search filters to csv
     */
    const exportToCsv = useCallback(async () => {
        const data = loadAllData();
        if (!isMounted() || !data)
            return;
        return formatExportingData(data);
    }, [loadAllData, isMounted, formatExportingData])

    /**
     * With each change in the [tab], updates the value of the [updatedTab] state.
     */
    useLayoutEffect(() => setUpdatedTab(searchData.tab), [searchData.tab])

    const tabsList = useMemo(() => ([
        {title: "Outstanding Balance", value: tabs.outstandingBalance},
        {title: "Zeroed out balance", value: tabs.all},
    ]), [])

    /**
     * Handles changes in the tabs of the orders view.
     */
    const onTabChanged = useCallback(async (e, newTab) => {
        if (selectedTransactions?.length) {
            const confirmed = await new Promise((resolve) => {
                dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                    open: true,
                    title: 'Confirm Filter Change',
                    description: 'You have transactions selected. Changing the tab will clear your selection. Do you want to proceed?',
                    confirmButtonText: 'Proceed',
                    cancelButtonText: 'Cancel',
                    maxWidth: 'sm',
                    fullWidth: false,
                    onClose: (confirmed) => {
                        resolve(confirmed);
                        return Promise.resolve(true);
                    }
                }));
            })
            if (!confirmed)
                return;
        }
        setData([]);
        dataRef.current = [];
        setSelectedTransactions([]);
        dataGridApi.current?.resetSelection(false);
        setTab(newTab);
    }, [setTab, dispatch, selectedTransactions?.length])


    /**
     * Opens the receipt dialog
     * @type {(function(*): void)|*}
     */
    const openReceiptDialog = useCallback((item) => {
        setReceiptDialogState({
            title: 'Transaction Receipt',
            open: true,
            customerId: item.customerId,
            // TODO: receipt id for the print receipt
            receiptId: item.receiptId,
            close: () => setReceiptDialogState(initReceiptDialogState)
        });
    }, [])

    const dataGridRows = useMemo(() => {
        return data?.map((item, index) => {
            const isDuplicate = data.find(i => i.recordId === item.recordId && i !== item);
            return ({
                key: `${item.recordId}${isDuplicate ? `-${index}` : ''}`,
                data: item,
                cells: {
                    [DataGridExoticColumnFields.selection]: null,
                    [AccountReceivableTransactionsDataGridColumnNames.receipt]:
                        item.type === "Payment" && item.receiptId && operatorAccessLevels.packages.sales.paymentReceipts ?
                            <IconButton
                                color={'paper.primary'}
                                onPressed={() => openReceiptDialog(item)}
                            >
                                <ReceiptLongRounded className={'icon normal'}/>
                            </IconButton>
                            : ' ',
                    [AccountReceivableTransactionsDataGridColumnNames.transactionNo]: item.transactionNo,
                    [AccountReceivableTransactionsDataGridColumnNames.date]: item.date,
                    [AccountReceivableTransactionsDataGridColumnNames.debit]: item.debit,
                    [AccountReceivableTransactionsDataGridColumnNames.credit]: item.credit,
                    [AccountReceivableTransactionsDataGridColumnNames.linkBalance]: item.linkBalance,
                    [AccountReceivableTransactionsDataGridColumnNames.balance]: item.balance,
                    [AccountReceivableTransactionsDataGridColumnNames.termsCode]: item.termsCode,
                    [AccountReceivableTransactionsDataGridColumnNames.customerPO]: item.customerPO,
                    [AccountReceivableTransactionsDataGridColumnNames.referenceNo]: item.referenceNo,
                    [AccountReceivableTransactionsDataGridColumnNames.type]: item.type,
                    [AccountReceivableTransactionsDataGridColumnNames.memo]: item.memo,
                }
            })
        })
    }, [data, openReceiptDialog, operatorAccessLevels.packages.sales.paymentReceipts])

    /**
     * Fetches the selected transactions form the selection state.
     * Results are always unique by recordId so connect payloads cannot include duplicate debit/credit ids.
     * @type {function(*, *, *): *[]}
     */
    const getSelectedTransactionsFromSelectionState = useCallback(async (selectedRows, excludedRows, allRowsSelected) => {
        if (allRowsSelected) {
            const excludedIds = excludedRows.map(row => row.data.recordId);
            if (dataRef.current.length === paginationInfoRef.current.length) {
                return uniqueByRecordId(dataRef.current.filter(item => !excludedIds.includes(item.recordId)));
            }
            disabledRef.current = true;
            setLoading(true);
            const allData = await loadAllData(true);
            if (!isMounted())
                return [];
            if (!allData) {
                setLoading(false);
                disabledRef.current = false;
                return [];
            }
            setData(() => {
                dataRef.current = allData;
                return allData;
            });
            // Keep server totalCount; advance currentPage so capacity-based hasMore treats the list as fully hydrated.
            setPaginationInfo(prevState => {
                const total = prevState.length || allData.length;
                const newState = ({
                    ...prevState,
                    currentPage: Math.max(1, Math.ceil(total / prevState.pageSize)),
                })
                paginationInfoRef.current = newState;
                return newState;
            })
            setLoading(false);
            setTimeout(() => {
                disabledRef.current = false;
            }, 1000)
            return uniqueByRecordId(allData.filter(item => !excludedIds.includes(item.recordId)));
        } else {
            const selectedIds = selectedRows.map(row => row.data.recordId);
            return uniqueByRecordId(dataRef.current.filter(item => selectedIds.includes(item.recordId)));
        }
    }, [isMounted, loadAllData, setLoading, setPaginationInfo])

    /**
     * Handles the change of the selected rows in the table to enable the actions in the footer based on the selected transactions
     * @type {(function(*, {selectedRows: *, excludedRows: *, allRowsSelected: *}): void)}
     */
    const onRowSelectionInfoChanged = useCallback(async (_, {selectedRows, excludedRows, allRowsSelected}) => {
        const selected = await getSelectedTransactionsFromSelectionState(selectedRows, excludedRows, allRowsSelected)
        if (!isMounted())
            return;
        setSelectedTransactions(selected ?? []);
    }, [getSelectedTransactionsFromSelectionState, isMounted])

    const dataGridFooterActions = useMemo(() =>
        /**@type {DataGridFooterAction[]}*/
        [
            {
                name: dataGridActions.connect,
                title: "Connect Selected Transactions (Apply)",
                visibilityConstraints: () => {
                    const lengthConstraint = selectedTransactions.length >= 2;
                    const minOneDebit = selectedTransactions.some(item => parseFloat(item.debit) > 0);
                    const minOneCredit = selectedTransactions.some(item => parseFloat(item.credit) > 0);
                    return lengthConstraint && minOneDebit && minOneCredit && searchData.tab === tabs.outstandingBalance;
                }
            },
            {
                name: dataGridActions.requestDisconnection,
                title: "Request Disconnection of Selected Transactions",
                visibilityConstraints: () => {
                    const lengthConstraint = selectedTransactions.length >= 2;
                    const minOneDebit = selectedTransactions.some(item => parseFloat(item.debit) > 0);
                    const minOneCredit = selectedTransactions.some(item => parseFloat(item.credit) > 0);
                    return lengthConstraint && minOneDebit && minOneCredit && searchData.tab === tabs.all;
                }
            }
        ], [searchData.tab, selectedTransactions])

    /**
     * Connects the selected transactions using the connection logic mentioned above.
     * Debit/credit record ids are unique by recordId before being sent to the API.
     */
    const connectTransactions = useCallback(async () => {
        const uniqueSelected = uniqueByRecordId(selectedTransactions);
        const checksumInCents = uniqueSelected.reduce((acc, item) => {
            return acc + toCents(item.debit) - toCents(item.credit);
        }, 0);

        const checkSumToZero = checksumInCents === 0;
        if (!checkSumToZero) {
            return ToastService.warning({
                title: "Cannot Connect Transactions",
                message: "The sum of the selected transactions must be zero in order to connect them together. Please review your selection.",
            })
        }
        setLoading(true);
        const response = await Api.connectArTransactions({
            customerId: defaultCustomer?.id,
            debitRecordIds: uniqueByRecordId(uniqueSelected.filter(item => parseFloat(item.debit ?? 0) > 0)).map(item => item.recordId),
            creditRecordIds: uniqueByRecordId(uniqueSelected.filter(item => parseFloat(item.credit ?? 0) > 0)).map(item => item.recordId),
        })
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            dataGridApi.current?.resetSelection(true);
            setSelectedTransactions([]);
            setData([]);
            dataRef.current = [];
            // Reset to page 1 so the next search replaces rows instead of appending onto stale data.
            const wasPageOne = (paginationInfoRef.current?.currentPage ?? 1) === 1;
            setPaginationInfo(prevState => {
                const newState = ({
                    ...prevState,
                    currentPage: 1,
                })
                paginationInfoRef.current = newState;
                return newState;
            })
            // When already on page 1, useSearch will not re-run from a page change — force a search.
            // When currentPage drops to 1, useSearch's effect re-fetches with the updated page.
            if (wasPageOne) {
                await Promise.all([
                    performSearch(),
                    fetchAccountReceivableInformation(),
                ])
            } else {
                await fetchAccountReceivableInformation();
            }
        }
    }, [defaultCustomer?.id, fetchAccountReceivableInformation, selectedTransactions, isMounted, performSearch, setLoading, setPaginationInfo])

    /**
     * Sends an email to the support team to request the disconnection of the selected transactions in order to be able to make adjustments on them and connect them again afterwards, as the system doesn't allow the user to edit or disconnect transactions that are linked together.
     * @type {(function(*): void)|*}
     */
    const sendDisconnectEmail = useCallback((selectedData) => {

        const subject = encodeURIComponent(`Disconnect AR Transactions ${defaultCustomer?.name ?? ""} - ${partnerInformation?.name ?? ""}`);
        const body = encodeURIComponent(
            `Dear BizKey Tech Support Team,\n\n` +
            `I want to request the disconnection of the following AR transactions for as I need to make adjustments on them and reconnect them again afterwards:\n\n` +
            `Transaction numbers to disconnect from each other: ${selectedData.map(item => item.transactionNo).join(', ')}\n` +
            `Customer Name: ${defaultCustomer?.name ?? 'N/A'}\n` +
            `Partner: ${partnerInformation?.name ?? ""}\n` +
            `Requesting Operator: ${operatorInformation?.name ?? ""}\n\n` +
            `Please proceed and inform me once the change is complete to update the part number using the system.`
        );
        const mailto = `mailto:support@bizkeytech.com?cc=info@bizkeytech.com,parsa@bizkeytech.com,amir@bizkeytech.com&subject=${subject}&body=${body}`;
        const link = document.createElement('a');
        link.href = mailto;
        link.target = '_blank';
        link.click();
    }, [defaultCustomer?.name, operatorInformation?.name, partnerInformation?.name])

    /**
     * Handles the action over the selected row in the table based on the name of the action
     * @param action
     * @return {Promise<void>}
     */
    const onActionInvoked = useCallback(async (action) => {
        switch (action.name) {
            case dataGridActions.connect:
                void connectTransactions();
                break;
            case  dataGridActions.requestDisconnection:
                sendDisconnectEmail();
                break;
            default:
                break;
        }
    }, [connectTransactions, sendDisconnectEmail])

    const schema = useMemo(() => {
        return Yup.object().shape({
            startDate: Yup.mixed()
                .validDate(ValidateMessages.incorrect)
                .maxDate(Yup.ref('endDate'), ValidateMessages.lessThan('End Date'))
                .nullable(),
            endDate: Yup.mixed()
                .nullable()
                .validDate(ValidateMessages.incorrect)
                .minDate(Yup.ref('startDate'), ValidateMessages.greaterThan('Start Date')),
        })
    }, []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        startDate: searchData.filters?.startDate ? DateUtils.serverToUTC(searchData.filters?.startDate) : undefined,
        endDate: searchData.filters?.endDate ? DateUtils.serverToUTC(searchData.filters?.endDate) : undefined,
    }), [searchData.filters?.endDate, searchData.filters?.startDate])

    /**
     * Handles the submission of the filters form to update the filters of the search and consequently update the shown data,
     * with a confirmation dialog in case there are selected transactions as changing the filters will clear the selection.
     * @type {(function(*): Promise<void>)}
     */
    const onFiltersSubmitted = useCallback(async (values) => {
        if (selectedTransactions?.length) {
            const confirmed = await new Promise((resolve) => {
                dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                    open: true,
                    title: 'Confirm Filter Change',
                    description: 'You have transactions selected. Changing the filters will clear your selection. Do you want to proceed?',
                    confirmButtonText: 'Proceed',
                    cancelButtonText: 'Cancel',
                    maxWidth: 'sm',
                    fullWidth: false,
                    onClose: (confirmed) => {
                        resolve(confirmed);
                        return Promise.resolve(true);
                    }
                }));
            })
            if (!confirmed)
                return;
        }
        setSelectedTransactions([]);
        dataGridApi.current?.resetSelection(false);
        setFilters({
            startDate: values.startDate ? DateUtils.UTCToServer(values.startDate).format(DateUtils.withoutOffsetFormat) : undefined,
            endDate: values.endDate ? DateUtils.UTCToServer(values.endDate).format(DateUtils.withoutOffsetFormat) : undefined,
        });
    }, [dispatch, selectedTransactions?.length, setFilters])

    return (
        <>
            <Row className='no-gutters w-100 justify-content-between align-items-center mt-4 mb-3'>
                <Typography variant={'h6'} fontWeight={600} marginInlineStart={1}>
                    {defaultCustomer?.name ?? ""}'s Account Receivable
                </Typography>
                {
                    !arInfoError &&
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end'}}>
                        <Button
                            variant={'outlined'}
                            color={'primary'}
                            type={'submit'}
                            size={'medium'}
                            startIcon={<SvgIcon className='icon medium' component={Printer}/>}
                            onClick={() => setStatementPrintingDialogState({open: true})}
                        >
                            Print Outstanding Balances
                        </Button>
                    </Box>
                }
            </Row>
            <Box sx={{width: '100%'}}>
                {
                    !!arInfoError &&
                    <Alert
                        severity={'error'}
                        sx={{marginBlockEnd: 2}}
                        action={
                            <Button
                                color={'inherit'}
                                size={'small'}
                                onClick={() => void fetchAccountReceivableInformation()}
                                disabled={loadingAccountReceivableInformation}
                            >
                                {t('retry')}
                            </Button>
                        }
                    >
                        <AlertTitle>{t('arInfoFetchErrorTitle')}</AlertTitle>
                        {arInfoError}
                    </Alert>
                }
                <Grid2 container spacing={1} sx={{justifyContent: 'space-evenly', marginInline: 0}}>
                    {
                        loadingAccountReceivableInformation &&
                        <>
                            <Grid2 xs={6} md={3} xl={1.71}>
                                <Skeleton width={'100%'} height={86} sx={{transform: 'none'}}/>
                            </Grid2>
                            <Grid2 xs={6} md={3} xl={1.71}>
                                <Skeleton width={'100%'} height={86} sx={{transform: 'none'}}/>
                            </Grid2>
                            <Grid2 xs={6} md={3} xl={1.71}>
                                <Skeleton width={'100%'} height={86} sx={{transform: 'none'}}/>
                            </Grid2>
                            <Grid2 xs={6} md={3} xl={1.71}>
                                <Skeleton width={'100%'} height={86} sx={{transform: 'none'}}/>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={1.71}>
                                <Skeleton width={'100%'} height={86} sx={{transform: 'none'}}/>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={1.71}>
                                <Skeleton width={'100%'} height={86} sx={{transform: 'none'}}/>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={1.71}>
                                <Skeleton width={'100%'} height={86} sx={{transform: 'none'}}/>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={2}>
                                <Skeleton width={'100%'} height={86} sx={{transform: 'none'}}/>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={2}>
                                <Skeleton width={'100%'} height={86} sx={{transform: 'none'}}/>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={2}>
                                <Skeleton width={'100%'} height={86} sx={{transform: 'none'}}/>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={2}>
                                <Skeleton width={'100%'} height={86} sx={{transform: 'none'}}/>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={2}>
                                <Skeleton width={'100%'} height={86} sx={{transform: 'none'}}/>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={2}>
                                <Skeleton width={'100%'} height={86} sx={{transform: 'none'}}/>
                            </Grid2>
                        </>
                    }
                    {
                        !loadingAccountReceivableInformation &&
                        <>
                            <Grid2 xs={6} md={3} xl={1.71}>
                                <Card variant={'outlined'} sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant={'body2'} fontWeight={500} component='span' color={'text.primary'}>
                                        Over 30:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={600} color={'secondary.main'}>
                                        {formatMoney(accountReceivableInformation?.over30 ?? 0)}
                                    </Typography>
                                </Card>
                            </Grid2>
                            <Grid2 xs={6} md={3} xl={1.71}>
                                <Card variant={'outlined'} sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant={'body2'} fontWeight={500} component='span' color={'text.primary'}>
                                        Over 60:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={600} color={'secondary.main'}>
                                        {formatMoney(accountReceivableInformation?.over60 ?? 0)}
                                    </Typography>
                                </Card>
                            </Grid2>
                            <Grid2 xs={6} md={3} xl={1.71}>
                                <Card variant={'outlined'} sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant={'body2'} fontWeight={500} component='span' color={'text.primary'}>
                                        Over 90:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={600} color={'secondary.main'}>
                                        {formatMoney(accountReceivableInformation?.over90 ?? 0)}
                                    </Typography>
                                </Card>
                            </Grid2>
                            <Grid2 xs={6} md={3} xl={1.71}>
                                <Card variant={'outlined'} sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant={'body2'} fontWeight={500} component='span' color={'text.primary'}>
                                        Over 120:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={600} color={'secondary.main'}>
                                        {formatMoney(accountReceivableInformation?.over120 ?? 0)}
                                    </Typography>
                                </Card>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={1.71}>
                                <Card variant={'outlined'} sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant={'body2'} fontWeight={500} component='span' color={'text.primary'}>
                                        Future:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={600} color={'secondary.main'}>
                                        {formatMoney(accountReceivableInformation?.future ?? 0)}
                                    </Typography>
                                </Card>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={1.71}>
                                <Card variant={'outlined'} sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant={'body2'} fontWeight={500} component='span' color={'text.primary'}>
                                        Current:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={600} color={'secondary.main'}>
                                        {formatMoney(accountReceivableInformation?.current ?? 0)}
                                    </Typography>
                                </Card>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={1.71}>
                                <Card variant={'outlined'} sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant={'body2'} fontWeight={500} component='span' color={'text.primary'}>
                                        Balance:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={600} color={'secondary.main'}>
                                        {formatMoney(accountReceivableInformation?.balance ?? 0)}
                                    </Typography>
                                </Card>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={2}>
                                <Card variant={'outlined'} sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant={'body2'} fontWeight={500} component='span' color={'text.primary'}>
                                        Last Payment Date:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={600} color={'secondary.main'}>
                                        {accountReceivableInformation?.lastPaymentDate ? DateUtils.serverToLocal(accountReceivableInformation.lastPaymentDate).format(DateUtils.dateTimeFormat) : '--'}
                                    </Typography>
                                </Card>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={2}>
                                <Card variant={'outlined'} sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant={'body2'} fontWeight={500} component='span' color={'text.primary'}>
                                        Last Payment Amount:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={600} color={'secondary.main'}>
                                        {accountReceivableInformation?.lastPaymentAmount ? formatMoney(accountReceivableInformation.lastPaymentAmount) : '--'}
                                    </Typography>
                                </Card>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={2}>
                                <Card variant={'outlined'} sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant={'body2'} fontWeight={500} component='span' color={'text.primary'}>
                                        Last Invoice Date:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={600} color={'secondary.main'}>
                                        {accountReceivableInformation?.lastInvoiceDate ? DateUtils.serverToLocal(accountReceivableInformation.lastInvoiceDate).format(DateUtils.dateTimeFormat) : '--'}
                                    </Typography>
                                </Card>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={2}>
                                <Card variant={'outlined'} sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant={'body2'} fontWeight={500} component='span' color={'text.primary'}>
                                        Terms:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={600} color={'secondary.main'}>
                                        {accountReceivableInformation?.terms ?? '--'}
                                    </Typography>
                                </Card>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={2}>
                                <Card variant={'outlined'} sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant={'body2'} fontWeight={500} component='span' color={'text.primary'}>
                                        Credit Limit:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={600} color={'secondary.main'}>
                                        {accountReceivableInformation?.creditLimit ? formatMoney(accountReceivableInformation.creditLimit) : '--'}
                                    </Typography>
                                </Card>
                            </Grid2>
                            <Grid2 xs={6} md={4} xl={2}>
                                <Card variant={'outlined'} sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant={'body2'} fontWeight={500} component='span' color={'text.primary'}>
                                        Average Days to Pay:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={600} color={'secondary.main'}>
                                        {accountReceivableInformation?.averageDaysToPay !== undefined ? accountReceivableInformation.averageDaysToPay : '--'}
                                    </Typography>
                                </Card>
                            </Grid2>
                        </>
                    }
                </Grid2>
            </Box>
            {
                !arInfoError &&
                <>
                    <div className={'w-100 my-5'}/>
                    <Typography variant={'subtitle1'} fontWeight={600} marginInlineStart={1}>
                        Transactions List
                    </Typography>
                    <div className={'w-100 pt-4'}/>
                    <Form onSubmit={onFiltersSubmitted} initialValues={initialValues} validate={validate}>
                        {({values}) => (
                            <Grid2 container spacing={2} sx={{marginInline: 0}}>
                                <Grid2 xs={12} md>
                                    <MuiRFF.DatePicker
                                        name={'startDate'}
                                        label={"Start Date"}
                                        required={required.startDate}
                                        maxDate={values.endDate}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md>
                                    <MuiRFF.DatePicker
                                        name={'endDate'}
                                        label={"End Date"}
                                        required={required.endDate}
                                        minDate={values.startDate}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={'auto'} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
                                    <Button variant={'contained'} color={'primary'} type={'submit'} size={'medium'}>
                                        Search
                                    </Button>
                                </Grid2>
                            </Grid2>
                        )}
                    </Form>
                    <div className='w-100 my-2'/>
                    <div className='w-100 flex-grow-1 mt-4'>
                        <Tabs value={searchData.tab} onChange={onTabChanged}>
                            {
                                tabsList.map(e => (
                                    <Tab key={e.value} value={e.value} label={e.title}/>
                                ))
                            }
                        </Tabs>
                    </div>
                    <div className='w-100 my-3'/>
                    <Card variant={'outlined'} sx={{display: 'flex', flexDirection: 'column', border: 'none', padding: 1}}>
                        <PartnerDataGrid
                            ref={dataGridApi}
                            {...dataGridSaveConfig}
                            initialLayoutState={{
                                columns: columns,
                            }}
                            columns={dataGridColumns}
                            footerActions={dataGridFooterActions}
                            onActionInvoked={onActionInvoked}
                            rows={dataGridRows}
                            onRowSelectionInfoChanged={onRowSelectionInfoChanged}
                            useInfiniteScroll={searchData.paginationInfo.length > data.length}
                            useVirtualizedRows={searchData.paginationInfo.length === data.length}
                            infiniteScrollHasMoreData={hasMoreToLoad}
                            state={{
                                sortBy: searchData.orderBy,
                                pagination: searchData.paginationInfo,
                                loading: {state: searchData.loading},
                                density: dataGridDensity ?? "compact",
                            }}
                            getExportInfo={canExportCustomerAccountReceivableTransactions ? exportToCsv : undefined}
                            onCurrentPageChanged={onCurrentPageChanged}
                            disablePaginationControls
                            hidePageSize
                            getPaginationText={(pagination) => `Viewing ${data.length} of ${pagination.length} transactions`}
                        />
                        {
                            searchData.tab === tabs.outstandingBalance &&
                            <Box sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end'}}>
                                <Divider flexItem orientation={'horizontal'}/>
                                <Card variant={'outlined'} sx={{padding: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 300, backgroundColor: 'secondary.lighter'}}>
                                    <Typography variant={'body2'} fontWeight={500} component='span' color={'text.primary'}>
                                        Total Amount:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={600} color={'secondary.main'}>
                                        {formatMoney(totalAmount)}
                                    </Typography>
                                </Card>
                            </Box>
                        }
                    </Card>
                </>
            }
            <TransactionReceiptDialog
                title={receiptDialogState.title}
                open={receiptDialogState.open}
                customerId={receiptDialogState.customerId}
                receiptId={receiptDialogState.receiptId}
                setOpen={receiptDialogState.close}
            />
            <PrintCustomerOutstandingStatementDialog
                open={statementPrintingDialogState.open}
                customerId={defaultCustomer?.id}
                customerName={defaultCustomer?.name}
                close={() => setStatementPrintingDialogState(initialStatementPrintingDialogState)}
            />
        </>
    )
}

export default CustomerAccountReceivableTransactionsView;
